import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { COPY } from '../constants/brand';

const PAIN_POINTS = {
  Dancer: COPY.problem.dancer,
  Artist: COPY.problem.artist,
  Studio: COPY.problem.studio,
  "I just love to Dance": COPY.problem.dancer,
  "I am an Artist": COPY.problem.artist,
  "I own a Studio": COPY.problem.studio,
};

export const RoleProblemSection = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPains, setSelectedPains] = useState([]);
  const [showConclusion, setShowConclusion] = useState(false);
  const currentPainPoints = selectedRole ? PAIN_POINTS[selectedRole] || [] : [];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setSelectedPains([]);
    setShowConclusion(false);
  };

  const handlePainSelect = (pain) => {
    const newPains = selectedPains.includes(pain)
      ? selectedPains.filter(p => p !== pain)
      : [...selectedPains, pain];
    
    setSelectedPains(newPains);

    // Show conclusion when at least 3 pains are selected
    if (newPains.length >= 3 && !showConclusion) {
      setShowConclusion(true);
    }
  };

  const frictionPercentage = currentPainPoints.length > 0
    ? Math.min((selectedPains.length / currentPainPoints.length) * 100, 100)
    : 0;

  const scrollToWaitlist = () => {
    const element = document.querySelector('#waitlist');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="problem" 
      className="pt-[4.5rem] pb-12 md:pt-24 md:pb-16 bg-aya-black"
      data-testid="problem-section"
    >
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          {/* Section Title */}
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-7 text-center">
            {COPY.problem.title}
          </h2>

          {/* Role Selector */}
          <div className="mb-12">
            <p className="text-white/90 text-xl md:text-2xl mb-8 text-center leading-tight">
              {COPY.problem.prompt}
            </p>
            
            <div 
              className="flex flex-wrap justify-center gap-3 md:gap-4"
              data-testid="role-selector"
            >
              {COPY.problem.roles.map((role) => (
                <button
                  key={role}
                  onClick={() => handleRoleSelect(role)}
                  data-testid={`role-btn-${role.toLowerCase()}`}
                  className={`
                    px-5 py-2.5 md:px-6 md:py-3 font-heading font-semibold text-sm md:text-base
                    border-2 transition-all duration-300
                    ${selectedRole === role 
                      ? 'bg-aya-yellow text-black border-aya-yellow' 
                      : 'bg-transparent text-white border-white/30 hover:border-white/60'
                    }
                  `}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          {/* Pain Points Cards */}
          <AnimatePresence mode="wait">
            {selectedRole && (
              <motion.div
                key={selectedRole}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="mb-8"
              >
                <p className="text-zinc-400 text-base mb-6 text-center">
                  Pick what sounds familiar
                </p>

                <div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                  data-testid="pain-points-grid"
                >
                  {currentPainPoints.map((pain, index) => (
                    <motion.button
                      key={pain}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handlePainSelect(pain)}
                      data-testid={`pain-card-${index}`}
                      className={`
                        p-4 text-left text-sm md:text-base
                        border transition-all duration-300
                        ${selectedPains.includes(pain)
                          ? 'bg-aya-yellow/10 border-aya-yellow text-white'
                          : 'bg-aya-surface border-aya-border text-zinc-300 hover:border-white/40'
                        }
                      `}
                    >
                      <span className="flex items-start gap-3">
                        <span className={`
                          w-5 h-5 flex-shrink-0 border-2 mt-0.5
                          flex items-center justify-center text-xs
                          transition-all duration-200
                          ${selectedPains.includes(pain)
                            ? 'border-aya-yellow bg-aya-yellow text-black'
                            : 'border-zinc-600'
                          }
                        `}>
                          {selectedPains.includes(pain) && '✓'}
                        </span>
                        {pain}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Friction Meter */}
          {selectedRole && selectedPains.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-8"
              data-testid="friction-meter"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-500 text-sm">Friction Level</span>
                <span className="text-aya-yellow font-mono text-sm">
                  {Math.round(frictionPercentage)}%
                </span>
              </div>
              <div className="h-2 bg-aya-surface rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${frictionPercentage}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-aya-yellow to-orange-500 rounded-full"
                />
              </div>
            </motion.div>
          )}

          {/* Conclusion */}
          <AnimatePresence>
            {showConclusion && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-center"
                data-testid="problem-conclusion"
              >
                <p className="text-xl md:text-2xl text-white font-semibold mb-6">
                  {COPY.problem.conclusion}
                </p>
                <button
                  onClick={scrollToWaitlist}
                  className="hero-outline-btn rounded border border-aya-yellow bg-transparent px-8 py-4 font-heading font-bold text-base text-aya-yellow"
                  data-testid="problem-cta-waitlist"
                >
                  Join the Waitlist
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default RoleProblemSection;
